"use client";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Flex } from "antd";
import Text from "antd/es/typography/Text";
import {
  hasConsultationRequired,
  resolveMemberId,
  applyMemberPricing,
  REQUEST_COPY,
  REQUIREMENT_COPY,
} from "@openings/booking-core";
import type { LocationOpeningsItem, DbTimeString } from "@openings/api-client";
import type { DbDateString } from "@openings/api-client";
import { bookingSectionTitleStyle, bookingSmallStyle } from "../bookingStyles";
import type {
  BookingAppointmentForm,
  BookingOpeningsClient,
  SelectedService,
} from "../types";

import { MemberPanel } from "./MemberPanel";
import { useScheduleMemberOpenings } from "./useScheduleMemberOpenings";

function appendMemberPageState(
  baseHref: string,
  memberId: string,
  scheduleId: string,
) {
  const isAbsolute = /^[a-z][a-z\d+.-]*:/i.test(baseHref);
  const url = new URL(baseHref, "https://openings.invalid");
  url.searchParams.set("member", memberId);
  url.searchParams.set("schedule", scheduleId);
  return isAbsolute
    ? url.toString()
    : `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Fetches & displays member openings for a single schedule in schedule mode.
 * Port of the web ScheduleBooking `MemberOpeningsDisplay` component.
 */
export function ScheduleMemberOpeningsDisplay({
  salonId,
  scheduleId,
  date,
  selectedServices,
  cancellationPolicyEnabled,
  cancellationPolicyHours,
  cancellationPolicyNote,
  lateGraceMinutes,
  verificationMethod,
  openingsClient,
  openAppointmentForm,
  onConsultationClick,
  refreshCount = 0,
  hasTeamMembers = true,
  showAvailabilityStatus = false,
  memberPageBaseHref,
}: {
  salonId: string;
  scheduleId: string;
  date: DbDateString;
  selectedServices: SelectedService[];
  cancellationPolicyEnabled?: boolean;
  cancellationPolicyHours?: number | null;
  cancellationPolicyNote?: string | null;
  lateGraceMinutes?: number | null;
  verificationMethod?: "SMS" | "EMAIL";
  openingsClient: BookingOpeningsClient;
  openAppointmentForm: (form: BookingAppointmentForm) => void;
  onConsultationClick: (member: LocationOpeningsItem) => void;
  refreshCount?: number;
  hasTeamMembers?: boolean;
  showAvailabilityStatus?: boolean;
  memberPageBaseHref?: string;
}) {
  const { loading, members: memberOpenings } = useScheduleMemberOpenings({
    openingsClient,
    salonId,
    scheduleId,
    date,
    selectedServices,
    refreshCount,
  });
  const selectionResetKey = `${scheduleId}:${date}:${refreshCount}:${selectedServices
    .map((service) => `${service.id}:${service.option?.id ?? ""}`)
    .join("|")}`;
  const [selectedSlot, setSelectedSlot] = useState<{
    memberKey: string;
    time: DbTimeString;
  } | null>(null);

  /* Cache the rendered section height so a refresh does not collapse the rail
   * while openings load. Measuring the real responsive grid avoids estimates
   * drifting when columns or avatars change. */
  const containerRef = useRef<HTMLDivElement>(null);
  const cachedHeightRef = useRef(0);

  useEffect(() => {
    if (loading || memberOpenings.length === 0 || !containerRef.current) return;

    const container = containerRef.current;
    const cacheHeight = () => {
      const height = container.getBoundingClientRect().height;
      if (height > 0) cachedHeightRef.current = height;
    };
    cacheHeight();

    const observer = new ResizeObserver(cacheHeight);
    observer.observe(container);
    return () => observer.disconnect();
  }, [loading, memberOpenings]);

  useEffect(() => {
    setSelectedSlot(null);
  }, [selectionResetKey]);

  // The selection itself says whether it needs a request: a member clone
  // takes its business service's `hasConsultation` (`resolveServicesForOwner`).
  // Branching on it before the openings arrive keeps "Choose a time" and its
  // loading row off a service nobody can book directly (BF-DRIFT-005). A
  // member's own flag still counts for a selection that does not say.
  const selectionRequestRequired = selectedServices.some(
    (service) => service.hasConsultation,
  );
  const isConsultation = useMemo(() => {
    return (
      selectionRequestRequired ||
      memberOpenings.some((member) =>
        hasConsultationRequired(member, selectedServices),
      )
    );
  }, [selectionRequestRequired, selectedServices, memberOpenings]);

  const handleTimeClick = useCallback(
    (member: LocationOpeningsItem, time: DbTimeString) => {
      const { schedule } = member;
      openAppointmentForm({
        salonId,
        userId: resolveMemberId(member),
        scheduleId: schedule.id,
        stylistName: member.name,
        memberTags: member.tags,
        scheduleAddress: schedule.address ?? "",
        services: applyMemberPricing(selectedServices, member.services),
        date,
        time,
        // The zone these openings were computed in, so the confirmation's
        // calendar event lands on the business's clock (BF-DRIFT-016).
        timezone: member.timezone,
        teamMemberId: member.teamMemberId ?? undefined,
        cancellationPolicyEnabled,
        cancellationPolicyHours,
        cancellationPolicyNote,
        lateGraceMinutes,
        verificationMethod,
      });
    },
    [
      salonId,
      selectedServices,
      date,
      cancellationPolicyEnabled,
      cancellationPolicyHours,
      cancellationPolicyNote,
      lateGraceMinutes,
      verificationMethod,
      openAppointmentForm,
    ],
  );

  const handleConsultationClick = useCallback(
    (member: LocationOpeningsItem) => {
      onConsultationClick(member);
    },
    [onConsultationClick],
  );

  // booking.requirement.request + booking.request.explanation: what the
  // request branch says in place of the time grid.
  const requestIntro = (
    <>
      <Text style={bookingSectionTitleStyle}>
        {REQUIREMENT_COPY["booking.requirement.request"]}
      </Text>
      <span style={bookingSmallStyle}>
        {REQUEST_COPY["booking.request.explanation"]}
      </span>
    </>
  );

  if (loading && selectionRequestRequired) {
    return (
      <Flex
        vertical
        gap="small"
        style={{
          width: "100%",
          minHeight: cachedHeightRef.current || undefined,
        }}
      >
        {requestIntro}
        <span role="status" style={bookingSmallStyle}>
          Loading…
        </span>
      </Flex>
    );
  }

  if (loading) {
    return (
      <Flex
        vertical
        gap={8}
        style={{
          width: "100%",
          minHeight: cachedHeightRef.current || undefined,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <Text style={bookingSectionTitleStyle}>Choose a time</Text>
          <span style={bookingSmallStyle}>Loading…</span>
        </div>
      </Flex>
    );
  }

  const membersWithSlots = memberOpenings.filter((m) => m.openings.length > 0);
  const membersWithoutSlots = memberOpenings.filter(
    (m) => m.openings.length === 0,
  );
  const isSingleService = selectedServices.length === 1;

  // Consultation mode
  if (isConsultation) {
    if (memberOpenings.length === 0) {
      return (
        <Flex vertical gap="small" style={{ width: "100%" }}>
          {requestIntro}
          <span style={{ ...bookingSmallStyle, fontStyle: "italic" }}>
            No team members are currently available for this service.
          </span>
        </Flex>
      );
    }

    return (
      <Flex vertical gap="small" style={{ width: "100%" }}>
        {requestIntro}
        <Flex vertical gap={8} style={{ width: "100%", marginTop: 4 }}>
          {memberOpenings.map((member) => (
            <MemberPanel
          salonId={salonId}
              key={resolveMemberId(member)}
              member={member}
              selectedServices={selectedServices}
              isConsultation={true}
              isSingleService={isSingleService}
              onSlotClick={handleTimeClick}
              onConsultationClick={handleConsultationClick}
              openingsClient={openingsClient}
              showAvailabilityStatus={showAvailabilityStatus}
              selectionResetKey={selectionResetKey}
              memberPageHref={
                memberPageBaseHref
                  ? appendMemberPageState(
                      memberPageBaseHref,
                      resolveMemberId(member),
                      scheduleId,
                    )
                  : undefined
              }
            />
          ))}
        </Flex>
      </Flex>
    );
  }

  // Normal openings mode
  if (membersWithSlots.length === 0 && membersWithoutSlots.length === 0) {
    return (
      <Flex vertical gap="small" style={{ width: "100%" }}>
        <Text style={bookingSectionTitleStyle}>Choose a time</Text>
        <span style={bookingSmallStyle}>
          {hasTeamMembers
            ? "No openings available for this day. Try another date."
            : "This schedule doesn\u2019t have online booking set up yet. Please contact the business directly to book."}
        </span>
      </Flex>
    );
  }

  return (
    <Flex ref={containerRef} vertical gap={8} style={{ width: "100%" }}>
      <Text style={bookingSectionTitleStyle}>Choose a time</Text>
      {membersWithSlots.map((member) => (
        <MemberPanel
          salonId={salonId}
          key={resolveMemberId(member)}
          member={member}
          selectedServices={selectedServices}
          isConsultation={false}
          isSingleService={isSingleService}
          onSlotClick={handleTimeClick}
          onConsultationClick={handleConsultationClick}
          openingsClient={openingsClient}
          showAvailabilityStatus={showAvailabilityStatus}
          selectionResetKey={selectionResetKey}
          memberPageHref={
            memberPageBaseHref
              ? appendMemberPageState(
                  memberPageBaseHref,
                  resolveMemberId(member),
                  scheduleId,
                )
              : undefined
          }
          selectedTime={
            selectedSlot?.memberKey === resolveMemberId(member)
              ? selectedSlot.time
              : null
          }
          onSelectedTimeChange={(time) =>
            setSelectedSlot({
              memberKey: resolveMemberId(member),
              time,
            })
          }
        />
      ))}
      {membersWithoutSlots.map((member) => (
        <MemberPanel
          salonId={salonId}
          key={resolveMemberId(member)}
          member={member}
          selectedServices={selectedServices}
          isConsultation={false}
          isSingleService={isSingleService}
          onSlotClick={handleTimeClick}
          onConsultationClick={handleConsultationClick}
          openingsClient={openingsClient}
          showAvailabilityStatus={showAvailabilityStatus}
          selectionResetKey={selectionResetKey}
          memberPageHref={
            memberPageBaseHref
              ? appendMemberPageState(
                  memberPageBaseHref,
                  resolveMemberId(member),
                  scheduleId,
                )
              : undefined
          }
          selectedTime={
            selectedSlot?.memberKey === resolveMemberId(member)
              ? selectedSlot.time
              : null
          }
          onSelectedTimeChange={(time) =>
            setSelectedSlot({
              memberKey: resolveMemberId(member),
              time,
            })
          }
        />
      ))}
    </Flex>
  );
}
